// askResearch — single round-trip client for the research-chat edge function's
// non-streaming (`stream:false`) branch (SLICE #7c-2, surface A — mobile Ask).
//
// The desktop surface consumes research-chat over SSE (see useResearchChatStream).
// Mobile prefers ONE request/response, so the edge function has an ADDITIVE
// `stream:false` branch that runs the same model/tool loop but buffers the answer
// + citations and returns a single JSON body. This helper wraps that call via
// `supabase.functions.invoke` (which auto-attaches the user JWT + apikey — no
// manual getSession needed) and normalises the result for the UI.
//
// This is a LIVE network call (the LLM) — it CANNOT work offline. Callers must
// gate on connectivity (useIsOnline) before invoking; we do NOT queue questions.

import { supabase } from "@/integrations/supabase/client";

/** Structured citation metadata returned alongside the answer, keyed by source_id. */
export interface AskCitation {
  source_id: string;
  title?: string | null;
  axis?: string | null;
  chunk_id?: string | null;
  extraction_id?: string | null;
  question_id?: string | null;
  score?: number | null;
}

export interface AskResult {
  /** Full assistant answer. May contain inline `[source: <uuid>]` markers. */
  answer: string;
  /** De-duplicated structured citations behind the answer. */
  citations: AskCitation[];
  runIds: string[];
  costUsd: number;
  turns: number;
  stopReason: string | null;
}

/** Strip raw `[source: <uuid>]` markers for plain-text display. Mirrors the desktop stripper. */
export function stripSourceMarkers(text: string): string {
  return text
    .replace(/\[source:\s*[0-9a-fA-F-]{8,}\]/g, "")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

/**
 * Put a single question to the research hub and get a cited answer in one round-trip.
 *
 * Throws on transport / HTTP / edge errors with a user-facing message (the same
 * 401/402/403 mapping the desktop hook surfaces). The caller shows the message via toast.
 */
export async function askResearch(query: string): Promise<AskResult> {
  const q = query.trim();
  if (!q) throw new Error("Type a question first.");

  // Generated types are stale for this function name; cast through `any`.
  const { data, error } = await (supabase.functions.invoke as any)("research-chat", {
    body: { query: q, stream: false },
  });

  if (error) {
    // supabase-js wraps non-2xx in a FunctionsHttpError carrying the Response.
    const status: number | undefined =
      (error as any)?.context?.status ?? (error as any)?.status;
    if (status === 401) throw new Error("Please sign in again.");
    if (status === 402) throw new Error("The hub is busy right now — try again shortly.");
    if (status === 403) throw new Error("You don't have access to the research hub.");
    throw new Error(
      (error as any)?.message || "Something went wrong answering that. Try again.",
    );
  }

  if (!data || typeof data !== "object") {
    throw new Error("Something went wrong answering that. Try again.");
  }

  // The edge may return a structured error object with a 200-ish envelope in
  // some failure modes; surface it rather than rendering an empty answer.
  if (typeof (data as any).error === "string" && !(data as any).answer) {
    throw new Error("Something went wrong answering that. Try again.");
  }

  const citationsRaw: any[] = Array.isArray((data as any).citations)
    ? (data as any).citations
    : [];
  // De-duplicate citations by source_id (the model may cite the same source
  // across turns; the edge pushes one per rerank hit).
  const seen = new Set<string>();
  const citations: AskCitation[] = [];
  for (const c of citationsRaw) {
    const sid = c?.source_id ? String(c.source_id) : "";
    if (!sid || seen.has(sid.toLowerCase())) continue;
    seen.add(sid.toLowerCase());
    citations.push({
      source_id: sid,
      title: c?.title ?? null,
      axis: c?.axis ?? null,
      chunk_id: c?.chunk_id ?? null,
      extraction_id: c?.extraction_id ?? null,
      question_id: c?.question_id ?? null,
      score: typeof c?.score === "number" ? c.score : null,
    });
  }

  return {
    answer: typeof (data as any).answer === "string" ? (data as any).answer : "",
    citations,
    runIds: Array.isArray((data as any).run_ids) ? (data as any).run_ids : [],
    costUsd: typeof (data as any).cost_usd === "number" ? (data as any).cost_usd : 0,
    turns: typeof (data as any).turns === "number" ? (data as any).turns : 0,
    stopReason:
      typeof (data as any).stop_reason === "string" ? (data as any).stop_reason : null,
  };
}
